import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, Check, ShieldCheck, Loader2, X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

/**
 * AriaEmbed — pagina chat full-screen montata in iframe sui siti esterni.
 * Reads ?token=... → carica config istanza Aria, costruisce persona, chat dinamica.
 * Cattura lead via POST /api/widget/lead. Rispetta Host First Policy server-side.
 */
export default function AriaEmbed() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const fromUrl = params.get("from") || (typeof window !== "undefined" ? window.location.href : "");
    const preview = params.get("preview") === "1";

    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(!!token);
    const [err, setErr] = useState(token ? "" : "Token mancante.");
    const [accepted, setAccepted] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [leadAsked, setLeadAsked] = useState(false);
    const [leadReason, setLeadReason] = useState(null);    // natural | no_match | newsletter
    const [leadSubmitted, setLeadSubmitted] = useState(false);
    const [ariaForSite, setAriaForSite] = useState(false);
    const [goalAchieved, setGoalAchieved] = useState(false);
    const [networkPartners, setNetworkPartners] = useState([]);
    const listRef = useRef(null);

    // Contesto pagina ricevuto dal parent (via URL param 'pc' base64 + postMessage refresh)
    const pageContextRef = useRef("");
    const pageUrlRef = useRef(fromUrl);
    useEffect(() => {
        // Decodifica initial page_context da URL param
        try {
            const pc = params.get("pc");
            if (pc) {
                pageContextRef.current = decodeURIComponent(escape(atob(pc)));
            }
        } catch (_e) { /* invalid b64 */ }
        // Listener postMessage per aggiornamenti SPA (parent invia ogni 5s)
        const onMsg = (ev) => {
            const d = ev.data || {};
            if (d.type === "aria:page_context") {
                if (typeof d.page_context === "string") pageContextRef.current = d.page_context.slice(0, 8000);
                if (typeof d.page_url === "string") pageUrlRef.current = d.page_url;
            }
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, [params]);

    useEffect(() => {
        document.title = "Aria";
    }, []);

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const res = await api.get(`/widget/${token}/config`);
                setCfg(res.data);
                const opening = openingFor(res.data);
                setMessages([{ role: "assistant", content: opening }]);
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, sending]);

    const send = async (text) => {
        const t = (text || input).trim();
        if (!t || sending || !cfg) return;
        const next = [...messages, { role: "user", content: t }];
        setMessages(next);
        setInput("");
        setSending(true);
        try {
            const res = await api.post(`/widget/${token}/chat`, {
                messages: next.map(({ role, content }) => ({ role, content })),
                session_id: sessionId,
                page_context: pageContextRef.current,
                page_url: pageUrlRef.current,
            });
            const d = res.data || {};
            setSessionId(d.session_id || sessionId);
            setMessages((m) => [...m, { role: "assistant", content: d.reply || "" }]);

            // Goal achieved → mostra messaggio successo se presente
            if (d.goal_achieved) {
                setGoalAchieved(true);
                if (Array.isArray(d.network_partners) && d.network_partners.length) {
                    setNetworkPartners(d.network_partners);
                } else if (Array.isArray(d.network_suggestions) && d.network_suggestions.length) {
                    setNetworkPartners(d.network_suggestions.map((c) => ({ matched_category: c, fallback: true })));
                }
            }
            // === Lead trigger CONTESTUALE: solo dal backend, mai automatico per N turni ===
            if (!leadSubmitted && d.wants_lead && d.lead_reason) {
                setLeadAsked(true);
                setLeadReason(d.lead_reason);
            }
            if (d.aria_for_site) setAriaForSite(true);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "Connessione lenta, riprovo? Riscrivimi pure." }]);
        } finally {
            setSending(false);
        }
    };

    const submitLead = async (data) => {
        await api.post(`/widget/lead`, {
            widget_token: token,
            visitor_url: fromUrl,
            session_id: sessionId || token,
            messages: messages.slice(-12),
            name: data.name,
            email: data.email,
            phone: data.phone,
            company: data.company || "",
            consent_marketing: data.consent_marketing,
            consent_privacy: true,
            goal_achieved: goalAchieved,
            business_profile: cfg?.business_profile || {},
            geo: cfg?.geo || {},
        });
        setLeadSubmitted(true);
        setLeadAsked(false);
        const success = leadReason === "newsletter"
            ? "Perfetto, ti scrivo solo le cose davvero utili."
            : (cfg?.goal && cfg.goal.success_message) || "Perfetto, ti contattiamo a breve.";
        setMessages((m) => [...m, { role: "assistant", content: success }]);
    };

    if (loading) {
        return <ShellWrapper><Center><Loader2 className="animate-spin text-[#9EC3FF]" /></Center></ShellWrapper>;
    }
    if (err || !cfg) {
        return (
            <ShellWrapper>
                <Center>
                    <div className="text-white/85 text-[14px]">Aria non disponibile</div>
                    <div className="text-white/45 text-[12px] mt-1">{err}</div>
                </Center>
            </ShellWrapper>
        );
    }

    // GDPR consent gate (sempre — anche in preview, salvo flag automatico)
    if (!accepted) {
        return (
            <ShellWrapper cfg={cfg}>
                <ConsentGate cfg={cfg} onAccept={() => setAccepted(true)} />
            </ShellWrapper>
        );
    }

    return (
        <ShellWrapper cfg={cfg}>
            <Header cfg={cfg} preview={preview} />

            <div ref={listRef} className="flex-1 px-4 py-3 space-y-2.5 overflow-y-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
                {messages.map((m, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: EASE }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-[88%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed" style={{
                            background: m.role === "user" ? "linear-gradient(135deg, rgba(79,139,255,0.24), rgba(43,90,255,0.18))" : "rgba(255,255,255,0.05)",
                            color: "rgba(255,255,255,0.95)",
                            border: m.role === "user" ? "1px solid rgba(79,139,255,0.32)" : "1px solid rgba(255,255,255,0.06)",
                        }} data-testid={`aria-msg-${m.role}`}>
                            <RenderEmbedMessage content={m.content} />
                        </div>
                    </motion.div>
                ))}
                {sending && <TypingIndicator />}

                {/* CTA esterna se goal raggiunto + cta_url presente */}
                {goalAchieved && cfg.goal?.cta_url && (
                    <a href={cfg.goal.cta_url} target="_blank" rel="noreferrer" className="block mt-3 px-4 py-3 rounded-xl text-white text-[13px] text-center transition hover:scale-[1.01]" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", fontFamily: "Geist, sans-serif", boxShadow: "0 14px 40px -12px rgba(79,139,255,0.55)" }} data-testid="aria-goal-cta">
                        {cfg.goal.cta_label || "Vai"} →
                    </a>
                )}

                {/* Network partners (solo dopo goal e se policy permette) */}
                {goalAchieved && networkPartners.length > 0 && (
                    <NetworkSuggestions partners={networkPartners} geo={cfg.geo} />
                )}

                {/* === Lead form contestuale === */}
                {leadAsked && !leadSubmitted && (
                    <LeadForm cfg={cfg} reason={leadReason} onClose={() => setLeadAsked(false)} onSubmit={submitLead} />
                )}

                {/* === Aria For Site cross-sell === */}
                {ariaForSite && (
                    <AriaForSitePitch onDismiss={() => setAriaForSite(false)} />
                )}
            </div>

            <ChatInput value={input} onChange={setInput} onSend={() => send()} disabled={sending} />

            {/* Footer "Vuoi Aria anche tu?" sempre visibile */}
            <a
                href="https://totalklick.it/aria"
                target="_blank"
                rel="noreferrer"
                className="block px-4 py-2.5 text-center border-t border-white/5 group transition hover:bg-white/[0.02]"
                style={{ fontFamily: "Geist, sans-serif" }}
                data-testid="aria-embed-install-cta"
            >
                <span className="text-[11px] uppercase tracking-[0.18em] text-[#C4B5FD] mr-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    vuoi aria anche tu?
                </span>
                <span className="text-white/85 text-[12.5px] group-hover:text-white">
                    Installami gratis sulla tua attività →
                </span>
            </a>

            {/* Footer GDPR microcopy */}
            <div className="px-4 py-2 text-center text-[10px] text-white/35 border-t border-white/5" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em" }}>
                powered by <span className="text-white/55">aria · totalklick</span> · gdpr-ready
            </div>
        </ShellWrapper>
    );
}

// ============== UI ==============

function ShellWrapper({ children, cfg }) {
    return (
        <div className="flex flex-col h-screen w-screen overflow-hidden" data-testid="aria-embed-shell" style={{
            background: "radial-gradient(ellipse 800px 600px at 50% 0%, rgba(79,139,255,0.12), transparent 65%), #05050F",
            color: "#fff",
            fontFamily: "Geist, Outfit, sans-serif",
        }}>
            {children}
            <style>{embedCSS}</style>
        </div>
    );
}

function Header({ cfg, preview }) {
    return (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 flex-none">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-none" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", boxShadow: "0 0 18px -2px rgba(79,139,255,0.6)" }}>
                <Sparkles size={15} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-white text-[13.5px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                    {cfg?.persona_name || "Aria"}
                </div>
                <div className="text-[10.5px] text-emerald-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · {cfg?.site_name}
                </div>
            </div>
            {preview && (
                <span className="text-[9.5px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full text-[#FBBF24]" style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", fontFamily: "JetBrains Mono, monospace" }}>preview</span>
            )}
        </div>
    );
}

function ConsentGate({ cfg, onAccept }) {
    return (
        <div className="flex-1 flex items-center justify-center px-6">
            <div className="max-w-[320px] text-center">
                <div className="inline-grid place-items-center w-14 h-14 rounded-2xl mb-4 mx-auto" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)" }}>
                    <ShieldCheck size={24} className="text-white" />
                </div>
                <div className="text-white text-[17px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                    Ciao, sono {cfg?.persona_name || "Aria"}
                </div>
                <p className="text-white/65 text-[12.5px] leading-relaxed mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                    L&apos;assistente di <b className="text-white">{cfg?.site_name}</b>. Prima di iniziare:
                    ti chiediamo il consenso al trattamento dei tuoi dati ex Reg. UE 2016/679.
                    Tratteremo solo email, telefono e conversazione, conservati per 24 mesi.
                </p>
                <button onClick={onAccept} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-white text-[13px] transition hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", fontFamily: "Geist, sans-serif", boxShadow: "0 14px 40px -10px rgba(79,139,255,0.5)" }} data-testid="aria-consent-accept">
                    Acconsento e inizia la chat
                </button>
                <a href="/legal/privacy-template-widget" target="_blank" rel="noreferrer" className="block text-[11px] mt-3 text-[#9EC3FF] hover:text-white underline">Leggi l&apos;informativa completa</a>
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex justify-start">
            <div className="px-3.5 py-2.5 rounded-2xl" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "240ms" }} />
                </span>
            </div>
        </div>
    );
}

function NetworkSuggestions({ partners, geo }) {
    return (
        <div className="rounded-2xl p-3 mt-2" style={{ background: "rgba(79,139,255,0.07)", border: "1px solid rgba(79,139,255,0.25)" }} data-testid="aria-network-suggestions">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#9EC3FF] mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>aria network · partner complementari</div>
            <div className="space-y-2">
                {partners.map((p, idx) => {
                    // Partner reale risolto
                    if (p.aria_instance_id) {
                        return (
                            <div key={p.aria_instance_id} className="rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`aria-network-partner-${idx}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                    <span className="text-white text-[13px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{p.site_name}</span>
                                    <span className="text-[9.5px] uppercase tracking-[0.18em] text-[#9EC3FF] ml-auto" style={{ fontFamily: "JetBrains Mono, monospace" }}>{humanize(p.matched_category || p.category)}</span>
                                </div>
                                <div className="text-white/55 text-[11px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    {[p.city, p.region].filter(Boolean).join(" · ") || geo?.city || "—"}
                                </div>
                                {p.site_url && (
                                    <a href={p.site_url} target="_blank" rel="noreferrer" className="inline-block mt-1.5 text-[11px] text-[#9EC3FF] hover:text-white underline">scopri {p.site_name} →</a>
                                )}
                            </div>
                        );
                    }
                    // Fallback: solo categoria astratta (nessun partner matchato nel DB)
                    return (
                        <div key={`cat-${idx}`} className="text-white/85 text-[12.5px]" data-testid={`aria-network-cat-${idx}`}>
                            → {humanize(p.matched_category || p)} {geo?.city ? <span className="text-white/45">· {geo.city}</span> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function LeadForm({ cfg, reason, onClose, onSubmit }) {
    const [data, setData] = useState({ name: "", email: "", phone: "", consent_marketing: reason === "newsletter" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const head = ({
        natural:    { label: "contatti per richiamarti", title: "Ti faccio richiamare" },
        no_match:   { label: "parla con un esperto umano", title: "Per il tuo caso ti passo un umano di " + (cfg?.site_name || "noi") },
        newsletter: { label: "resto a portata di mano",   title: "Lascia un contatto, ti scriverò solo cose utili" },
    })[reason] || { label: "contatti per richiamarti", title: "Ti faccio richiamare" };

    const set = (k, v) => setData((p) => ({ ...p, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await onSubmit({ ...data, lead_reason: reason });
        } catch (er) {
            setErr(formatApiError(er));
        } finally {
            setBusy(false);
        }
    };

    return (
        <motion.form
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="mx-1 my-2 rounded-2xl p-3.5 space-y-2 relative"
            style={{ background: "linear-gradient(135deg, rgba(79,139,255,0.13), rgba(43,90,255,0.06))", border: "1px solid rgba(79,139,255,0.38)" }}
            data-testid="aria-embed-lead-form"
            data-reason={reason || "natural"}
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute top-2 right-2 w-7 h-7 rounded-full grid place-items-center text-white/55 hover:text-white hover:bg-white/8 transition"
                aria-label="Chiudi form"
                data-testid="aria-embed-lead-close"
            >
                <X size={13} />
            </button>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#9EC3FF] pr-7" style={{ fontFamily: "JetBrains Mono, monospace" }}>{head.label}</div>
            <div className="text-white/95 text-[14px] pb-1" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{head.title}</div>
            <input required placeholder="Nome e cognome *" value={data.name} onChange={(e) => set("name", e.target.value)} className="embed-input" data-testid="aria-embed-name" />
            <input required type="email" placeholder="Email *" value={data.email} onChange={(e) => set("email", e.target.value)} className="embed-input" data-testid="aria-embed-email" />
            <input
                required={reason !== "newsletter"}
                type="tel"
                placeholder={reason === "newsletter" ? "Telefono (opz.)" : "Telefono *"}
                value={data.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="embed-input"
                data-testid="aria-embed-phone"
            />
            <label className="flex items-start gap-1.5 text-[11px] text-white/65 cursor-pointer">
                <input type="checkbox" checked={data.consent_marketing} onChange={(e) => set("consent_marketing", e.target.checked)} className="mt-0.5" style={{ accentColor: "#4F8BFF" }} data-testid="aria-embed-mkt" />
                <span>{reason === "newsletter" ? "Voglio ricevere newsletter e aggiornamenti utili." : "Acconsento anche al marketing diretto. Non obbligatorio."}</span>
            </label>
            <div className="text-[10.5px] text-white/40 leading-snug">Inviando accetti l&apos;<a className="text-[#9EC3FF] underline" href="/legal/privacy-template-widget" target="_blank" rel="noreferrer">informativa privacy</a> · titolare: {cfg?.site_name}.</div>
            {err && <div className="text-rose-300 text-[11px]">{err}</div>}
            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full text-white text-[13px] transition disabled:opacity-50" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", fontFamily: "Geist, sans-serif", boxShadow: "0 10px 30px -8px rgba(79,139,255,0.55)" }} data-testid="aria-embed-submit">
                {busy ? "Invio…" : reason === "newsletter" ? <>Tienimi aggiornato <Check size={11} /></> : <>Fammi richiamare <Check size={11} /></>}
            </button>

            {/* Micro-CTA cross-sell sotto al form */}
            <a
                href="https://totalklick.it/aria"
                target="_blank"
                rel="noreferrer"
                className="block mt-2 px-3 py-2.5 rounded-xl text-center group transition hover:scale-[1.01]"
                style={{
                    background: "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(79,139,255,0.06))",
                    border: "1px dashed rgba(196,181,253,0.4)",
                    fontFamily: "Geist, sans-serif",
                }}
                data-testid="aria-embed-install-cta-inline"
            >
                <div className="text-[#C4B5FD] text-[10.5px] uppercase tracking-[0.18em] mb-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    Vuoi Aria anche tu?
                </div>
                <div className="text-white text-[12.5px] inline-flex items-center gap-1.5 group-hover:gap-2 transition-all">
                    Installami gratis sulla tua attività →
                </div>
            </a>
        </motion.form>
    );
}

function RenderEmbedMessage({ content }) {
    if (!content) return null;
    const lines = String(content).split(/\r?\n/);
    const out = [];
    let listBuf = [];
    const flushList = (k) => {
        if (!listBuf.length) return;
        out.push(
            <ul key={`l-${k}`} className="my-1.5 space-y-1.5">
                {listBuf.map((t, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid="aria-embed-checklist-item">
                        <span className="mt-1 w-3.5 h-3.5 rounded-sm border border-[#9EC3FF]/60 flex-none" style={{ background: "rgba(79,139,255,0.05)" }} />
                        <span className="flex-1">{t}</span>
                    </li>
                ))}
            </ul>
        );
        listBuf = [];
    };
    lines.forEach((ln, idx) => {
        const m = ln.match(/^\s*▢\s+(.*)$/);
        if (m) {
            listBuf.push(m[1].trim());
        } else {
            flushList(idx);
            if (ln.trim()) out.push(<p key={`p-${idx}`} className="mb-1.5 last:mb-0">{ln}</p>);
        }
    });
    flushList("end");
    return <>{out}</>;
}

function AriaForSitePitch({ onDismiss }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mx-1 my-2 rounded-2xl p-3.5 relative"
            style={{
                background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(43,90,255,0.08))",
                border: "1px solid rgba(124,58,237,0.35)",
            }}
            data-testid="aria-embed-for-site-card"
        >
            <button
                type="button"
                onClick={onDismiss}
                className="absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center text-white/45 hover:text-white"
                aria-label="Chiudi proposta"
                data-testid="aria-embed-for-site-close"
            >
                <X size={12} />
            </button>
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#C4B5FD] mb-1 pr-6" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                Aria sul tuo sito
            </div>
            <div className="text-white text-[14px] mb-2" style={{ fontFamily: "Geist, sans-serif" }}>
                Vuoi che io curi anche il tuo sito?
            </div>
            <p className="text-white/65 text-[12px] mb-2.5 leading-relaxed">
                Posso ascoltare i tuoi visitatori 24/7 e darti lead già qualificati. Gratis, GDPR-ready.
            </p>
            <a href="https://totalklick.it/aria" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12px] transition hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #7C3AED, #4F8BFF)", fontFamily: "Geist, sans-serif", boxShadow: "0 10px 30px -10px rgba(124,58,237,0.55)" }} data-testid="aria-embed-for-site-cta">
                Scopri come →
            </a>
        </motion.div>
    );
}

function ChatInput({ value, onChange, onSend, disabled }) {
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSend(); }} className="flex items-center gap-2 px-4 py-3 border-t border-white/5 flex-none">
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Scrivi un messaggio…" disabled={disabled} className="flex-1 bg-transparent text-white text-[13.5px] placeholder:text-white/35 focus:outline-none px-2" style={{ fontFamily: "Outfit, sans-serif" }} autoFocus data-testid="aria-embed-input" />
            <button type="submit" disabled={!value.trim() || disabled} className="w-9 h-9 rounded-full grid place-items-center flex-none transition disabled:opacity-40" style={{ background: value.trim() ? "linear-gradient(135deg, #4F8BFF, #2B5AFF)" : "rgba(255,255,255,0.06)" }} data-testid="aria-embed-send">
                <Send size={14} className="text-white" />
            </button>
        </form>
    );
}

function Center({ children }) {
    return <div className="flex-1 flex items-center justify-center flex-col gap-3 text-center px-6">{children}</div>;
}

// ============== HELPERS ==============

function openingFor(cfg) {
    const persona = cfg.persona_name || `Aria ${cfg.vertical || ""}`;
    const site = cfg.site_name || "questo sito";
    const goalType = cfg.goal?.type || "lead";
    // Apertura empatica per vertical
    const vert = (cfg.vertical || "").toLowerCase();
    const verts = {
        ristorante: `Ciao, sono ${persona}. ${site} è il mio mondo. Cerchi un tavolo, info su menù o un evento?`,
        tipografia: `Ciao, sono ${persona}. Stampa, urgenze o preventivi: dimmi cosa ti serve.`,
        palestra: `Ciao, sono ${persona}. Vuoi info su corsi, abbonamenti o una prova gratuita?`,
        agenzia: `Ciao, sono ${persona}. Social, ads, contenuti? Raccontami il tuo brand.`,
        ecommerce: `Ciao, sono ${persona}. Ti aiuto con prodotti, spedizioni o un ordine?`,
        hotel: `Ciao, sono ${persona}. Disponibilità, prezzi, eventi: dimmi quando arrivi.`,
    };
    return verts[vert] || `Ciao, sono ${persona}, l'assistente di ${site}. Come posso aiutarti?`;
}

function humanize(slug) {
    return (slug || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const embedCSS = `
.embed-input{width:100%;padding:8px 10px;border-radius:8px;border:1px solid rgba(255,255,255,0.1);background:rgba(255,255,255,0.04);color:#fff;font-family:'Geist','Outfit',sans-serif;font-size:12.5px;transition:all .15s;}
.embed-input:focus{outline:none;border-color:rgba(79,139,255,0.55);background:rgba(255,255,255,0.06);}
.embed-input::placeholder{color:rgba(255,255,255,0.4);}
`;
